<template>
  <!-- La raíz queda en su lugar (Vue la usa de referencia al redibujar el padre); solo
       la hoja se muda a <body>, una vez, y se muestra/oculta con v-show. -->
  <span class="m-sheet-anchor">
  <div v-show="value" ref="sheet" class="m-sheet" @keydown.esc="close">
    <div class="m-sheet__backdrop" @click="close"></div>
    <div
      class="m-sheet__panel"
      :class="{ 'is-full': full }"
      role="dialog"
      aria-modal="true"
      :aria-label="title"
    >
      <div v-if="!full" class="m-sheet__handle" aria-hidden="true"></div>
      <div v-if="title || full" class="m-sheet__header">
        <button
          v-if="full"
          type="button"
          class="m-sheet__close"
          :aria-label="closeLabel"
          @click="close"
        >
          <i class="fas fa-times" aria-hidden="true"></i>
        </button>
        <h2>{{ title }}</h2>
        <slot name="header-action" />
      </div>
      <div class="m-sheet__body">
        <slot />
      </div>
      <div v-if="$slots.footer" class="m-sheet__footer">
        <slot name="footer" />
      </div>
    </div>
  </div>
  </span>
</template>

<script>
/**
 * Hoja que sube desde abajo (acciones) o pantalla completa (`full`, para filtros).
 * Se monta en <body> para que ningún contenedor del layout la recorte, y bloquea el
 * scroll de la página mientras está abierta. v-model = abierta/cerrada.
 */
export default {
  name: "MobileBottomSheet",
  props: {
    value: { type: Boolean, default: false },
    title: { type: String, default: "" },
    full: { type: Boolean, default: false },
    closeLabel: { type: String, default: "Close" },
  },
  watch: {
    value: {
      immediate: true,
      handler(open) {
        if (typeof document === "undefined") return;
        document.body.classList.toggle("m-sheet-open", !!open);
      },
    },
  },
  mounted() {
    document.body.appendChild(this.$refs.sheet);
  },
  beforeDestroy() {
    if (this.value) document.body.classList.remove("m-sheet-open");
    const sheet = this.$refs.sheet;
    if (sheet && sheet.parentNode) sheet.parentNode.removeChild(sheet);
  },
  methods: {
    close() {
      this.$emit("input", false);
      this.$emit("close");
    },
  },
};
</script>

<style>
body.m-sheet-open {
  overflow: hidden;
}
</style>

<style scoped>
.m-sheet-anchor {
  display: none;
}
.m-sheet {
  position: fixed;
  inset: 0;
  z-index: 1060;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}
.m-sheet__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(var(--bs-black-rgb), 0.4);
}
.m-sheet__panel {
  position: relative;
  max-height: 85vh;
  max-height: 85dvh;
  display: flex;
  flex-direction: column;
  background: var(--m-surface);
  color: var(--m-text);
  border-radius: 1rem 1rem 0 0;
  padding-bottom: env(safe-area-inset-bottom);
  box-shadow: var(--bs-box-shadow-lg);
}
.m-sheet__panel.is-full {
  height: 100%;
  max-height: none;
  border-radius: 0;
}
.m-sheet__handle {
  width: 2.25rem;
  height: 0.25rem;
  margin: 0.5rem auto 0.15rem;
  border-radius: 999px;
  background: var(--m-border);
}
.m-sheet__header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  padding-top: calc(0.6rem + env(safe-area-inset-top));
  border-bottom: 1px solid var(--m-border);
}
.m-sheet__panel:not(.is-full) .m-sheet__header {
  padding-top: 0.4rem;
}
.m-sheet__header h2 {
  flex: 1;
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
}
.m-sheet__close {
  border: 0;
  background: transparent;
  color: var(--m-text);
  width: 2.5rem;
  height: 2.5rem;
  font-size: 1.1rem;
}
.m-sheet__body {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
.m-sheet__footer {
  padding: 0.75rem 1rem;
  border-top: 1px solid var(--m-border);
  background: var(--m-surface);
}
</style>
